
// import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import { useTheme } from "@mui/material/styles";
import { count, getSetting, isDefined } from "../../config/helpers";
import dynamic from "next/dynamic";
import { useSelector } from "react-redux";
import useWindowSize from "../../_hooks/useWindowSize";

import CategoryPageLoader from "./CategoryPageLoader";
import { useCategoryContext } from "../Providers/CategoryContext";
import { useMemo } from "react";
import HomeCategories from "../HomeComponent/HomeCategories";
import useCategoryShallowLink from "@components/_hooks/CSRHooks/useCategoryShallowLink";

const PageHead = dynamic(() => import("../Reusables/PageHead"));
const BoxContainer = dynamic(() => import("../Reusables/BoxContainerV2"));

const CategorySliderContainer = dynamic(() => import("./CategoryHeading/CategorySliderContainer"), {
    loading: () => (
        <>
            <Skeleton variant="rectangle" sx={{ display: { xs: 'none', lg: 'block' }, padding: 0, margin: 1, height: 250 }} />
            <Skeleton sx={{ margin: 1 }} />
            <Skeleton sx={{ margin: 1 }} />
        </>
    )
    , ssr: true
});

const SubCategoryList = dynamic(() => import("./CategoryHeading/SubCategoryList"));
const CategorySchema = dynamic(() => import("../Schemas/CategorySchema"));
const CategorySelectedFilters = dynamic(() => import("./Filters/CategorySelectedFilters"), { ssr: false });
const CategorySortAndFilters = dynamic(() => import("./Filters/CategorySortAndFilters"));
const CategoryDescriptions = dynamic(() => import("./CategoryHeading/CategoryDescriptions"));
const CategoryPagination = dynamic(() => import("./CategoryPagination"));
const CategoryProducts = dynamic(() => import("./Products/CategoryProducts"), {
    loading: (e) => <CategoryPageLoader />
}
);
// const CategorySortAndFilters  = dynamic(() => import("./Filters/CategorySortAndFilters"));
const CategoryLongDescription = dynamic(() => import("./CategoryLongDescription"));


const Category = ({ categoryData, loading }) => {
    //categoryData
    //selected_sort_name - property of categoryData
    const {
        long_term,
        template_page_settings,
    } = categoryData;

    const { template_settings, pageSpinner } = useSelector((state) => state.globalReducer);
    const { categoryState, categoryLoaders } = useSelector((state) => state.categoryReducer);


    //constants
    const theme = useTheme();
    const { isMobile } = useWindowSize();

    const {
        loadingProducts,
        selected_category_filters,
        categoryProducts,
        catData,
        loadMoreProducts,
        show_pagination,
        page,
        showSubcategoryTiles
    } = useCategoryContext();

    const { handleShallowLink } = useCategoryShallowLink();

    const shouldShowCanonical =
        !!catData?.category?.slug &&
        (
            process.env.brand.brand_id != 1 || show_pagination === true
        );

    const canonical_link = !shouldShowCanonical
        ? false
        : show_pagination && page > 1
            ? `${process.env.NEXT_PUBLIC_HOME_URL}/${catData.category.slug}?page=${page}`
            : `${process.env.NEXT_PUBLIC_HOME_URL}/${catData.category.slug}`;
    // const canonical_link = (process.env.brand.brand_id == 1 || !catData?.category?.slug) ? false : `${process.env.NEXT_PUBLIC_HOME_URL}/${catData.category.slug}`
    // useEffect(() => {
    //     updateData(categoryData); //TODO: manage this state
    // }, [categoryData])

    //layout setting retrieved from template settings
    const layout = getSetting(
        template_settings,
        template_page_settings,
        "layout",
        "wide"
    );

    const isLoading = (loadingProducts.current || loading || categoryLoaders?.fetchingCategory || pageSpinner);
    const className = (!isMobile && template_page_settings.page_class_type ? template_page_settings.page_class_type : 'pl-0 pr-0');


    // console.log(template_page_settings)

    //type is the key of the page setting which will result in the width, e.g 800px
    //layout width is only applicable for custom layout
    //if layout is custom then the layout width in the page settings is checked if it is not null use it 
    //else check for the brand custom layout width value use if not null else return 0px
    // const layout_width =
    //     layout == "custom"
    //         ? getSetting(
    //               template_settings,
    //               template_page_settings,
    //               "custom_layout_width",
    //               "0px"
    //           )
    //         : "0px";

    //item count style

    if (isDefined(catData.term) && catData?.term != "") return; //Wait to load category if coming from search
    const meta_title = show_pagination && page > 1 ? `${catData.category.meta_title} - Page ${page}` : catData.category.meta_title;
    const meta_description = show_pagination && page > 1 ? `${catData.category.meta_description}. View additional products on page ${page}` : catData.category.meta_description;


    // Filter subcategories and chunk into sections for home page tiles
    const subCategories = useMemo(() => {
        if (!showSubcategoryTiles) return null;
        const showSubCategories = catData?.category?.sons.filter(son => son.visible == 'yes');
        const arr = [];
        const chunkSize = 3;
        let currentChunk = 1;
        for (let i = 0; i < showSubCategories.length; i += chunkSize) {
            const chunk = showSubCategories.slice(i, i + chunkSize);
            arr[currentChunk] = chunk;
            currentChunk++;
        }
        return arr;
    }, [showSubcategoryTiles])

    return (
        <>
            <PageHead
                title={meta_title}
                description={meta_description}
                keywords={catData.category.meta_keywords}
                link={canonical_link}
                ogTitle={meta_title}
                ogDescription={meta_description}
                ogUrl={`${process.env.NEXT_PUBLIC_HOME_URL}/${catData.category.slug}`}
                ogType="website"
                ogImage={catData.category.image}
            />

            <BoxContainer id="category_page" className={className}>
                {/* {addLayout(toWrapComponents, layout, layout_width)} */}
                <CategorySchema
                    categoryData={catData}
                    category_products={categoryProducts}
                    long_term={long_term}
                />

                {!isMobile && <CategorySliderContainer
                    slides={catData.slides}
                    loading={isLoading}
                />}



                <CategoryDescriptions
                    subtitle={catData?.category?.subtitle || catData.category.name}
                    description={catData?.category?.description_short || ""}
                    loading={isLoading}
                    page={page}
                />

                <>
                    {isDefined(catData.category.sons) && count(catData.category.sons) > 0 && !showSubcategoryTiles && (
                        <SubCategoryList sons={catData.category.sons} />
                    )}

                </>
                {(show_pagination && !isLoading && !showSubcategoryTiles) && (
                    <CategoryPagination
                        count={catData?.paginator?.last_page}
                        page={page}
                        from={catData?.paginator?.from}
                        to={catData?.paginator?.to}
                        total={catData?.paginator?.total}
                        per_page={template_page_settings?.per_page}
                    />
                )}

                {!showSubcategoryTiles && (
                    <CategorySortAndFilters
                        in_drawer={true}
                        filter_width={"250px"}
                    />
                )}

                <Divider className="mt-2 mb-2" />

                {selected_category_filters && Object.keys(selected_category_filters).length > 0 && (
                    <CategorySelectedFilters
                        theme={theme}
                    />
                )
                }

                {!isLoading && showSubcategoryTiles && Object.keys(subCategories).length > 0 && (
                    <div className="mobile-p-2 p-0">
                        <HomeCategories
                            categories={subCategories}
                            template_page_settings={template_page_settings}
                            source="category"
                            gridProps={{
                                xs: 6,
                                md: 4,
                                lg: 4,
                            }}
                            onLinkClick={handleShallowLink}

                        />
                    </div>
                )}

                {(!showSubcategoryTiles || isLoading) && (
                    <CategoryProducts
                        loadingProducts={isLoading}
                        products={categoryProducts}
                        template_page_settings={template_page_settings}
                        long_term={long_term}
                        props={catData}
                        source="category"
                    />
                )}

                {/* {show_pagination && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={loadMoreProducts}
                    >
                        Load More
                    </Button>
                )} */}

                {(show_pagination && !isLoading && !showSubcategoryTiles) && (
                    <CategoryPagination
                        count={catData?.paginator?.last_page}
                        page={page}
                        from={catData?.paginator?.from}
                        to={catData?.paginator?.to}
                        total={catData?.paginator?.total}
                        per_page={template_page_settings?.per_page}
                    />
                )}

                <CategoryLongDescription
                    layout={layout}
                    long_desc={catData.long_desc}
                    isMobile={isMobile}
                />
            </BoxContainer>
        </>
    );
};

export default Category;
